import { useQuery } from '@tanstack/react-query';

import { getQueryKey } from '@/api/utils/getQueryKey';
import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

export interface LeaseExtractionData {
  tenant: {
    email: string;
    phone: string;
    last_name: string;
    first_name: string;
  };
  address: string;
  pet_fee: {
    monthly_fee: number | null;
    one_time_fee: number | null;
  };
  due_date: string;
  end_date: string;
  late_fee: {
    amount: number;
    grace_period_days: number;
    additional_daily_fee: number | null;
  };
  base_rent: number;
  utilities: {
    included: boolean;
    tenant_responsible: boolean;
  };
  start_date: string;
  parking_fee: {
    monthly_fee: number | null;
  };
  amenities_fee: {
    monthly_fee: number | null;
  };
  other_charges: {
    key_replacement_fee: number | null;
    maintenance_call_fee: number | null;
    early_termination_fee: number | null;
  };
  payment_method: string;
  rent_adjustments: {
    annual_increase: number | null;
    renewal_options: string | null;
  };
  security_deposit: {
    amount: number;
    held_by: string;
    refund_conditions: string;
  };
  total_monthly_amount_due: number;
}

export interface LeaseExtractionResponse {
  data: LeaseExtractionData;
  success: boolean;
}

const METHOD = 'POST';
const ENDPOINT = 'lease/extract/:id';
export const QUERY_KEY = (id: string) => getQueryKey(METHOD, ENDPOINT, { id });

export const useLeaseExtract = (id: string, enabled = true) => {
  const { session } = useAuth();

  return useQuery<LeaseExtractionData>({
    queryKey: QUERY_KEY(id),
    queryFn: async () => {
      const response = await handleApiCall<LeaseExtractionResponse>(
        session,
        METHOD,
        getResolvedEndpoint(ENDPOINT, {
          id: id.toString(),
        })
      );

      return response.data;
    },
    enabled: enabled && !!session?.access_token,
  });
};
